<template>
  <div
    class="col full-height box-border-transfer q-pb-sm q-mt-xs attendance-transfer"
    style="max-width: 240px"
    v-show="listEntryNumbers.length"
  >
    <div class="text-primary row q-pt-sm q-pb-xs q-ml-xs q-pl-xs">
      <q-btn
        data-testid="transferencia-opcoes-fechar"
        flat
        unelevated
        size="xs"
        padding="0"
        color="negative"
        class=" q-mr-sm"
        @click="$emit('close-transfer-options')"
        icon="mdi-close-circle"
      >
        <q-tooltip>Fechar</q-tooltip>
      </q-btn>

      <div style="font-size: 16px;">
        Transferência
      </div>
    </div>

    <div class="row height-30px q-mb-sm">
      <div class="col q-px-xs">
        <div class="row full-height content-center">
          <SearchIcon class="search-icon q-ma-sm absolute" />

          <input
            data-testid="transferencia-busca"
            type="text"
            class="fit input-search font-search"
            v-model="filter"
            placeholder="busca..."
          >
        </div>
      </div>
    </div>

    <div
      dense
      class="rounded-borders full-width q-px-xs"
    >
      <template v-for="item in items">
        <div
          :data-testid="`transferencia-numero-${item.id}`"
          :class="`row items-center item-slice reverse ${canTransfer(item) ? 'cursor-pointer' : 'cursor-block'}`"
          :key="`transfer-${item.id}`"
          @click="() => canTransfer(item) ? $emit('on-transfer-number', item) : null"
        >
          <div class="col-auto q-pr-sm">
            <q-icon
              name="mdi-arrow-right-bold-circle-outline q-mr-xs"
              size="xs"
              :color="labelColor(item)"
              class="col-auto"
            />
          </div>

          <div class="col q-px-sm tw-min-w-0">
            <q-item-label class="ellipsis">
              <span :class="`text-${labelColor(item)}`">{{ item.number }} {{ labelStatus(item) }}</span>
            </q-item-label>

            <q-item-label
              caption
              class="ellipsis"
            >
              {{ labelType(item) }} {{ labelDesc(item) }}
            </q-item-label>
          </div>

          <q-tooltip
            :content-class="`bg-${labelColor(item)}`"
            content-style="font-size: 12px"
            anchor="center left"
          >
            <span v-if="canTransfer(item)">Transferir para</span>
            <span
              v-else
              class="text-bold"
            >Não é possível transferir</span>
            <br>
            <span class="text-bold">#{{ item.number }}</span>
            <br>
            {{ labelType(item) }} {{ labelDesc(item) }}
          </q-tooltip>
        </div>
      </template>

      <template v-if="!items.length && filter.length > 7">
        <q-item
          data-testid="transferencia-numero-externo"
          v-ripple
          clickable
          @click="$emit('on-transfer-number', { number: filter, destinationType: 'EXTERNAL' })"
        >
          <q-item-section>
            <div class="row items-center">
              <div class="col">
                <q-item-label>
                  {{ filter }} - Nº Externo
                </q-item-label>

                <q-item-label caption>
                  Número Externo
                </q-item-label>
              </div>

              <div class="col-auto">
                <q-icon
                  name="mdi-arrow-collapse"
                  color="oragen"
                  size="md"
                  class="q-pa-none"
                />
              </div>
            </div>

            <q-tooltip>
              Transferir para {{ filter }}
              <br>
              Número Externo
            </q-tooltip>
          </q-item-section>
        </q-item>

        <q-separator class="q-my-sm" />
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import SearchIcon from '@/assets/icons/search.svg'
import { entryNumberType } from '@/libs/transferUtils'
import getEntryNumbers from '@/libs/getEntryNumbers'

const canTransfer = (item) => {
  const agentStatus = item.status || ''
  const extensionStatus = item.extension?.status || ''

  if (['OFFLINE', 'PAUSED'].includes(agentStatus)) return false
  if (['Unreachable', 'Unknown'].includes(extensionStatus)) return false

  if ((!agentStatus && !extensionStatus) && (item.agentId || item.extensionId)) return false

  return true
}

export default {
  components: {
    SearchIcon
  },

  data () {
    return {
      filter: '',
      showFilter: '',
      entryNumbers: []
    }
  },

  mounted () {
    const vm = this
    vm.elMounted = true

    getEntryNumbers(true).catch(() => ({})).then((entryNumbers) => {
      if (!vm.elMounted) return
      vm.entryNumbers = entryNumbers
      if (!vm.entryNumbers.length) vm.$emit('close-transfer-options')
    })
  },

  beforeDestroy () {
    const vm = this
    vm.elMounted = false
  },

  computed: {
    ...mapState({
      listEntryNumbers ({ sipModule }) {
        const sipExtensions = sipModule?.extensions ?? {}
        const agents = this.entryNumbers.filter((i) => Boolean(i.agentId && i.extensionId)).reduce((acc, i) => Object.assign(acc, { [i.extensionId]: i.status }), {})

        return this.entryNumbers.map((data) => {
          const item = structuredClone(data)

          if (['URA', 'QUEUE'].includes(data.destinationType)) { item.status = 'ONLINE'; return item }
          if (!item.extensionId) return item

          const sipExtensionInfo = sipExtensions[item.extensionId]?.status ?? item.extension?.status
          if (sipExtensionInfo && item.extension) item.extension.status = sipExtensionInfo || 'OFFLINE'

          if (data.profile === 'CALLCENTER' || data.profile === 'WEB') item.status = agents[item.extensionId] || 'OFFLINE'

          return item
        }).sort((a, b) => Number(canTransfer(b)) - Number(canTransfer(a)))
      }
    }),

    items () {
      const vm = this
      const clearStr = (str) => String(str || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()
      const arg = clearStr(vm.filter)

      if (arg.length > 7) return 0

      let items = vm.listEntryNumbers.filter((item) => {
        const number = clearStr(item.number)
        const description = clearStr(item.description)
        return number.includes(arg) || description.includes(arg)
      })

      if (!items.length) {
        items = vm.entryNumbers.filter((item) => {
          const number = clearStr(item.number)
          const description = clearStr(item.description)
          return arg.includes(number) || arg.includes(description)
        })
      }

      return items
    }
  },

  methods: {
    labelType (item) {
      return entryNumberType(item)
    },

    canTransfer,

    labelStatus (item) {
      const agentStatus = item.status || ''
      const extensionStatus = item.extension?.status || ''

      let status = agentStatus || ''

      if (agentStatus) {
        if (agentStatus === 'OFFLINE') return 'Offline'
        if (agentStatus === 'PAUSED') return 'Pausado'

        if (agentStatus === 'ONLINE') status = 'Disponível'
        if (agentStatus === 'ONTHEPHONE') status = 'Atendendo'
      }

      if (!agentStatus || agentStatus === 'ONLINE') {
        if (extensionStatus === 'Ready') status = 'Conectado'
        if (extensionStatus === 'Reachable') status = 'Conectado'
        if (extensionStatus === 'Unavailable') status = 'Sem conexão'
        if (extensionStatus === 'Unreachable') status = 'Sem conexão'
        if (extensionStatus === 'Lagged') status = 'Instável'
        if (extensionStatus === 'Unknown') status = 'Desconectado'
        if (extensionStatus === 'On the phone') status = 'Atendendo'
      }

      if (!status && (item.agentId || item.extensionId)) status = 'Desconectado'

      return status ? ` - ${status}` : ''
    },

    labelColor (item) {
      const agentStatus = item.status || ''
      const extensionStatus = item.extension?.status || ''

      let status = agentStatus ? 'blue-8' : ''

      if (agentStatus) {
        if (agentStatus === 'OFFLINE') return 'red-8'
        if (agentStatus === 'PAUSED') return 'red-8'

        if (agentStatus === 'ONLINE') status = 'green-8'
        if (agentStatus === 'ONTHEPHONE') status = 'blue-8'
      }

      if (!agentStatus || agentStatus === 'ONLINE') {
        if (extensionStatus === 'Ready') status = 'green-8'
        if (extensionStatus === 'Reachable') status = 'green-8'
        if (extensionStatus === 'Unreachable') status = 'red-8'
        if (extensionStatus === 'Unavailable') status = 'red-8'
        if (extensionStatus === 'Lagged') status = 'orange-8'
        if (extensionStatus === 'Unknown') status = 'grey-8'
        if (extensionStatus === 'On the phone') status = 'grey-8'
      }

      if (!status && (item.extensionId || item.agentId)) status = 'red-8'

      return status || 'green-8'
    },

    labelDesc (item) {
      const number = String(item.number || '').trim().toLowerCase()
      const description = String(item.description || '').trim().toLowerCase()
      return number === description ? '' : item.description
    }
  }
}

</script>

<style lang="sass" scoped>
  .height1px
    height: 1px

  .cursor-block
    cursor: not-allowed !important

  .item-slice
    border-bottom: 1px solid #ddd
    padding: 5px 0 5px
    margin-bottom: 5px

  .bullet
    border-radius: 3px

  .box-border-transfer
    border-top-left-radius: 5px
    border-top-right-radius: 0
    border-bottom-left-radius: 5px
    border-bottom-right-radius: 0
    border-left: 1.4px solid #cacaca
    border-top: 1.4px solid #cacaca
    border-bottom: 1.4px solid #cacaca

  .attendance-transfer
    position: relative
    background: #FFFFFF

    .input-search
      border: none
      border: 1px solid #CDDCE5
      border-radius: 3px
      padding-left: 28px
      color: #01253C
      &:focus
        border: 1px solid #1769B6
        outline: none
      &:hover
        border: 1px solid #cacaca
      &::-webkit-input-placeholder
        color: #01253C
        opacity: 0.5
        font-style: normal
        font-weight: normal
        font-size: 12px
        line-height: 12px

    .search-icon
      height: 16px
      width: 16px

    .search-icon > stroke
      fill: #01253C66

    .height-30px
      height: 30px
      min-height: 30px
</style>
